import { useMemo, useState, useRef, useEffect } from "react";
import { Bar, ComposedChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, ReferenceLine } from "recharts";
import getModuleName from "utils/getModuleName";
import TooltipInfo from "components/Tooltip";
import './bar-chart-box.css';

const COLORS = [
   "#0095DA",
   "#6D6E71",
   "#001C54",
   "#F28C00",
   "#000000"
]

const ALLOWED_COLORS = [
   { hex: "#0095DA", name: "299 C" },
   { hex: "#6D6E71", name: "Cool Gray 10 C" },
   { hex: "#001C54", name: "295 C" },
   { hex: "#F28C00", name: "Orange 021" },
   { hex: "#000000", name: "Black" },
]

/** Odstęp od góry SVG — miejsce na etykietę max na osi Y (bez „wciskania” w krawędź). */
const CHART_MARGIN_TOP = 22;

function formatYAxisTickLabel(value, module) {
   const n = Number(value);
   if (Number.isNaN(n)) return String(value);
   const formattedValue = n === 0 ? "0" : Math.round(n).toString();
   return module === "UD" ? `${formattedValue}%` : formattedValue;
}

/** Etykieta osi Y na środku linii siatki (bez domyślnego dy z Recharts). */
function YAxisTickAligned({ x, y, payload, module }) {
   if (payload?.value === undefined || payload?.value === null) return null;
   return (
      <text
         x={x}
         y={y}
         textAnchor="end"
         className="y-axis-tick-text"
         dominantBaseline="middle"
         fontSize={14}
      >
         {formatYAxisTickLabel(payload.value, module)}
      </text>
   );
}

function getColorForCategory(categoryName, allCategories, colorPalette, seriesIndex) {
   const palette = (colorPalette && colorPalette.length) ? colorPalette : COLORS;

   // Prefer indeks serii (kolejność z API/legendy), jeśli dostępny
   if (typeof seriesIndex === "number" && seriesIndex >= 0) {
      return palette[seriesIndex % palette.length];
   }

   // Jeśli mamy legendę i pasujący indeks, użyj go (bez sortowania)
   if (Array.isArray(allCategories)) {
      const idx = allCategories.indexOf(categoryName);
      if (idx >= 0) {
         return palette[idx % palette.length];
      }
   }

   // Fallback: deterministyczny hash po nazwie
   if (typeof categoryName === "string") {
      let hash = 0;
      for (let i = 0; i < categoryName.length; i++) {
         hash = ((hash << 5) - hash) + categoryName.charCodeAt(i);
         hash = hash & hash;
      }
      return palette[Math.abs(hash) % palette.length];
   }

   return palette[0];
}

function ColorPickerAction({ currentColor, onChange, title, allowedColors }) {
   const [open, setOpen] = useState(false);
   const ref = useRef(null);
   const palette = useMemo(() => {
      if (Array.isArray(allowedColors) && allowedColors.length) {
         return allowedColors.map(c => typeof c === "string" ? { hex: c, name: c } : c);
      }
      return ALLOWED_COLORS;
   }, [allowedColors]);

   useEffect(() => {
      if (!open) return;
      const handler = (e) => {
         if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
   }, [open]);

   return (
      <div ref={ref} className="color-picker-action">
         <button
            type="button"
            className={`color-picker-trigger ${open ? "open" : ""}`}
            onClick={() => setOpen(prev => !prev)}
            title={title || "Kliknij, aby zmienić kolor"}
            aria-label={title || "Kliknij, aby zmienić kolor"}
            aria-expanded={open}
            aria-haspopup="listbox"
         >
            <span
               className="legend-swatch"
               aria-hidden="true"
               style={{
                  display: "inline-block",
                  width: "26px",
                  height: "26px",
                  backgroundColor: currentColor || "#0095DA",
                  border: `1px solid rgba(0,0,0,0.12)`,
                  borderRadius: "4px",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                  transition: "box-shadow 0.15s"
               }}
            />
            <span className="color-picker-arrow" aria-hidden="true" />
         </button>
         {open && (
            <div className="color-picker-dropdown">
               <span className="color-picker-label">Kolor wykresu:</span>
               <div className="color-picker-grid">
                  {palette.map((c, idx) => (
                     <button
                        key={c.hex || idx}
                        className={`color-option${currentColor?.toLowerCase() === c.hex.toLowerCase() ? ' selected' : ''}`}
                        style={{ '--swatch-color': c.hex }}
                        onClick={() => { onChange(c.hex); setOpen(false); }}
                        title={c.name || c.hex}
                        aria-label={`Wybierz kolor: ${c.name || c.hex}`}
                     >
                        <span className="color-option-inner" style={{ background: c.hex }} />
                     </button>
                  ))}
               </div>
            </div>
         )}
      </div>
   );
}

function CustomTooltip({ active, payload, label, bars_amount, show_average, legend }) {
   
   const isVisible = active && payload && !!payload.length;
   if ( !isVisible ) return;

   const module = getModuleName();
   
   // Znajdź element payload odpowiadający słupkowi (nie linii średniej)
   // payload może zawierać elementy dla Bar i Line, więc musimy znaleźć właściwy
   const barPayload = payload.find(p => p.dataKey && p.dataKey.startsWith('data-item')) || payload[0];
   const linePayload = show_average ? payload.find(p => p.dataKey === 'average') : null;
   
   const value_as_percent = barPayload?.payload?.value_as_percent;
   const average = show_average && linePayload ? linePayload?.payload?.average : null;
   const average_raw = show_average && linePayload ? linePayload?.payload?.average_raw : null;
   const bars_labels = [];
   
   const values = [];
   if ( bars_amount > 0 ) {
      for ( let i = 0; i < bars_amount; i++ ) {
         const is_null = barPayload?.payload[ "data-item" + i + "-null" ];
         const v = is_null ? "b/d": Number( barPayload?.payload?.[ "data-item" + i ]) || 0;
         values.push( v );
      }
   }

   if ( bars_amount > 1 ) {
      for ( let i = 0; i < bars_amount; i++ ) {
         const extra_value = barPayload?.payload?.[ "data-item" + i + "-extra" ];
         const value = values[i]+ ( value_as_percent ? "%" : "" );
         const extra = extra_value ? `(${ extra_value })` : "";
         bars_labels.push(
            <p key={ i }> { legend[i]}: { value } { extra } </p>
         )
      }
   }

   // Dla pojedynczego słupka, znajdź wartość z odpowiedniego elementu payload (słupka, nie linii)
   const singleBarPayload = payload.find(p => p.dataKey === 'data-item0') || barPayload;
   const is_single_null = singleBarPayload?.payload?.["data-item0-null"];
   const single_extra_value = singleBarPayload?.payload?.[ "data-item0-extra" ]; 
   // Użyj wartości z payload słupka, nie z payload[0] (który może być z linii)
   const single_value_raw = is_single_null ? null : (singleBarPayload?.value !== undefined ? singleBarPayload.value : singleBarPayload?.payload?.["data-item0"]);
   const single_value = is_single_null ? "b/d" : ( Number( single_value_raw )) + ( value_as_percent ? "%" : "" );
   const single_extra = single_extra_value ? `(${ single_extra_value })` : "";

   // Format średniej dla UD: tylko wartość procentowa
   const formatAverage = () => {
      if (!average) return null;
      
      if (module === "UD") {
         return `${average}%`;
      }
      
      return average;
   };

   return (
      <div className="custom-tooltip">
         <>
            { bars_amount === 1 && <p>{`${ label } : ${ single_value } ${ single_extra }`} </p> }
            { bars_amount > 1 && label && <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>{ label }</p> }
            { bars_labels }
            { average && <p> Średnia: { formatAverage() } </p> }
         </>
      </div>
   )
}

export default function BarChartBox({ heading, captureHeading, data, legend, series_colors, allowed_colors, bars_amount, show_average, show_main_lines, show_auxiliary_lines, key_variable_tooltip, onColorChange }) {
   
   const module = getModuleName();
   
   // Oblicz czy etykiety powinny być pionowe
   const vertical_labels = useMemo(() => {
      // Jeśli jest dużo danych, zawsze używaj pionowych etykiet
      if (data?.length >= 4) return true;
      
      if (!data || data.length === 0) return false;
      
      // Sprawdź długość etykiet - jeśli są bardzo długie, użyj pionowych
      const maxLabelLength = Math.max(...data.map(item => item.label?.length || 0));
      
      // Jeśli najdłuższa etykieta ma więcej niż 30 znaków, użyj pionowych etykiet
      if (maxLabelLength > 30) return true;
      
      return false;
   }, [data]);

   // Sprawdź czy wszystkie dane są null (dla całego wykresu)
   const hasNoData = useMemo(() => {
      if (!data || !Array.isArray(data) || data.length === 0) return true;
      
      return data.every(item => {
         for (let i = 0; i < bars_amount; i++) {
            const isNull = item[`data-item${i}-null`];
            if (!isNull) return false; // Jeśli choć jedna wartość nie jest null, zwróć false
         }
         return true; // Wszystkie wartości dla tego elementu są null
      });
   }, [data, bars_amount]);

   const longest_label_l = useMemo(() => {
      if ( !data ) return 0;
      const l = Math.max(...data.map( item => item.label?.length || 0 ));
      return l > 30 ? 30 : l;
   }, [ data ]);

   const labels_height = longest_label_l * 4.5 < 30 ? 30 : longest_label_l * 4.5;

   // Dynamiczna wysokość obszaru wykresu (bez labeli i marginesów)
   const chartAreaHeight = useMemo(() => {
      // Wysokość kontenera .chart-wrap to 600px (z CSS)
      // Marginesy: góra ~20px, dół = labels_height (jeśli są etykiety pionowe) lub ~10px
      // Wysokość obszaru wykresu = 600 - marginesy
      const containerHeight = 600;
      const topMargin = 20;
      const bottomMargin = vertical_labels ? labels_height : 10;
      return containerHeight - topMargin - bottomMargin;
   }, [labels_height, vertical_labels]);

   // Oblicz min i max wartości dla YAxis (dla obsługi wartości ujemnych w AZZKU)
   const { yAxisDomain, showZeroLine } = useMemo(() => {
      if (!data || !Array.isArray(data) || data.length === 0) return { yAxisDomain: undefined, showZeroLine: false };
      
      let minValue = Infinity;
      let maxValue = -Infinity;
      let hasValues = false;
      
      data.forEach(item => {
         for (let i = 0; i < bars_amount; i++) {
            const isNull = item[`data-item${i}-null`];
            if (!isNull) {
               const value = Number(item[`data-item${i}`]);
               if (!isNaN(value)) {
                  hasValues = true;
                  if (value < minValue) minValue = value;
                  if (value > maxValue) maxValue = value;
               }
            }
         }
      });
      
      if (!hasValues) return { yAxisDomain: undefined, showZeroLine: false };
      
      // Oblicz padding dla obu stron
      const padding = Math.max(Math.abs(maxValue - minValue) * 0.1, 5);
      
      let domainMin, domainMax;
      
      // Jeśli wszystkie wartości są dodatnie (minValue >= 0), domain zaczyna się od 0
      if (minValue >= 0) {
         domainMin = 0;
         domainMax = maxValue + padding;
      }
      // Jeśli wszystkie wartości są ujemne (maxValue <= 0), domain kończy się na 0
      else if (maxValue <= 0) {
         domainMin = minValue - padding;
         domainMax = 0;
      }
      // Jeśli wykres zawiera zarówno wartości dodatnie jak i ujemne, domain obejmuje zero z paddingiem
      else {
         domainMin = minValue - padding;
         domainMax = maxValue + padding;
      }
      
      // Dla modułu UD, maksymalna wartość na osi Y to 100%
      if (module === "UD" && domainMax > 100) {
         domainMax = 100;
      }
      
      const domain = [domainMin, domainMax];
      
      // Pokaż linię zero jeśli zero jest w zakresie domain (czyli wykres przekracza zero)
      const needsZeroLine = domainMin < 0 && domainMax > 0;
      
      return { yAxisDomain: domain, showZeroLine: needsZeroLine };
   }, [data, bars_amount, module]);

   const colorPalette = series_colors || COLORS;
   const pickerPalette = allowed_colors || colorPalette;

   const Bars = useMemo(() => {
      const result = [];
      for ( let i = 0; i < bars_amount; i++ ) {
         const value = "data-item" + i;
         const categoryName = legend && legend[i] ? legend[i] : null;
         const color = getColorForCategory(categoryName, legend, colorPalette, i);
         const barProps = {
            dataKey: value,
            fill: color
         };
         
         // Dla modułów UD i AZZKU używamy własnej funkcji shape (dla obsługi null/"Brak danych")
         if (module === "UD" || module === "AZZKU") {
            barProps.shape = (props) => {
               const { payload, x, y, width, height } = props;
               
               // Sprawdź czy props są poprawne
               if (!payload || x === undefined || y === undefined || width === undefined) {
                  return null;
               }
               
               const isNull = payload[`data-item${i}-null`];
               const value = payload[`data-item${i}`];
               
               // Sprawdź czy wszystkie wartości w tej serii (dla danego i) są null
               const allNullInSeries = data.every(item => item[`data-item${i}-null`]);
               
               if (isNull) {
                  // Wyświetl "Brak danych" tylko jeśli wszystkie wartości w serii są null
                  if (true) {
                     
                     // Oblicz pozycję napisu na podstawie domeny osi Y
                     // W SVG: y=0 to góra, większe y to dół
                     // Pozycja `y` w props to pozycja, gdzie kolumna powinna być (dla wartości dodatnich to góra, dla ujemnych to dół)
                     let chartCenterY;
                     
                     if (yAxisDomain && Array.isArray(yAxisDomain) && yAxisDomain.length === 2) {
                        const [domainMin, domainMax] = yAxisDomain;
                        
                        // Jeśli wszystkie wartości są ujemne, napis na dole wykresu
                        if (domainMax <= 0) {
                           // Dla wartości ujemnych: y to dolna krawędź prostokąta
                           // Umieść napis na dole wykresu (poniżej y, bo y jest już na dole)
                           chartCenterY = y + 70; // 30px poniżej dolnej krawędzi
                        }
                        // Jeśli wszystkie wartości są dodatnie, napis na górze wykresu
                        else if (domainMin >= 0) {
                           // Dla wartości dodatnich: y to górna krawędź prostokąta
                           // Umieść napis na górze wykresu (powyżej y, bo y jest już na górze)
                           chartCenterY = y - 70; // 30px poniżej górnej krawędzi (w dół od y)
                        }
                        // Jeśli mieszane wartości, napis w środku
                        else {
                           // Wykres przekracza zero, umieść napis w środku obszaru wykresu
                           // Użyj pozycji y jako punktu odniesienia i przesuń w górę o połowę wysokości
                           chartCenterY = y;
                        }
                     } else {
                        // Fallback: środek wykresu względem pozycji y
                        chartCenterY = y;
                     }
                     
                     
                     const textX = x + width / 2;
                     
                     return (
                        <g>
                           <text
                              x={textX}
                              y={chartCenterY}
                              textAnchor="middle"
                              dominantBaseline="central"
                              transform={`rotate(-90, ${textX}, ${chartCenterY})`}
                             fontSize="16"
                              fill="#666"
                              fontWeight="600"
                           >
                              Brak danych
                           </text>
                        </g>
                     );
                  }
                  
                  // Jeśli w serii są zarówno null jak i nie-null wartości, nie wyświetlaj nic (przezroczysty prostokąt)
                  return (
                     <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height !== undefined && height !== null ? Math.abs(height) : 1}
                        fill="transparent"
                     />
                  );
               }
               
               // Dla wartości nie-null: 
               // - Dla UD: wszystkie wartości są dodatnie, normalne renderowanie
               // - Dla AZZKU: wartości mogą być ujemne
               // 
               // WAŻNE: W Recharts dla wartości ujemnych:
               // - `y` to pozycja DOLNEJ krawędzi prostokąta (dla wartości ujemnych)
               // - `height` to wartość UJEMNA (np. -100 dla wartości -100)
               // 
               // W SVG rect zawsze rysuje prostokąt w DÓŁ od pozycji y z dodatnim height.
               // Więc dla wartości ujemnych: musimy obliczyć pozycję górnej krawędzi jako y + height
               // (bo height jest ujemne, więc y + height = y - |height|) i użyć Math.abs(height) jako wysokości.
               if (height !== undefined && height !== null) {
                  let rectY = y;
                  let rectHeight = height;
                  
                  // Dla wartości ujemnych: height jest ujemne, więc musimy dostosować pozycję
                  if (height < 0) {
                     // y to dolna krawędź prostokąta, więc górna krawędź to y + height (bo height jest ujemne)
                     rectY = y + height;
                     rectHeight = Math.abs(height);
                  }
                  
                  const categoryName = legend && legend[i] ? legend[i] : null;
                  const barColor = getColorForCategory(categoryName, legend, colorPalette, i);
                  
                  return (
                     <rect
                        x={x}
                        y={rectY}
                        width={width}
                        height={rectHeight}
                        fill={barColor}
                     />
                  );
               }
               
               return null;
            };
         }
         
         result.push(<Bar key={i} {...barProps} />);
      }

      return result;
   }, [ bars_amount, module, data, chartAreaHeight, yAxisDomain, legend, colorPalette ]);

   const LineComponent = useMemo(() => {
      return show_average
         ? <Line 
            type="linear"
            isAnimationActive={ false }
            dataKey="average" 
            strokeWidth={ 4 }
            stroke="#00258B"
            dot={{ strokeWidth: 8 }}
         />
         : null
   }, [ show_average ])


   const renderSplitHeading = (text, className) => {
      const parts = text.includes("|")
         ? text.split("|").map((part) => part.trim()).filter(Boolean)
         : [text.trim()].filter(Boolean);
      if (parts.length === 0) return null;
      const HEADING_MAIN_EM = "1.08em";
      const HEADING_SUB_EM = "0.95em";
      return (
         <div className={`bar-chart-heading-split ${className || ""}`} style={{ marginBottom: "12px" }}>
            {parts.map((part, index) => {
               const isMain = index === 0 && parts.length > 1;
               return (
               <div
                  key={index}
                  style={{
                     fontSize: isMain ? HEADING_MAIN_EM : HEADING_SUB_EM,
                     fontWeight: isMain ? "600" : "500",
                     lineHeight: isMain ? "1.28" : "1.2",
                     marginBottom: index < parts.length - 1 ? "2px" : "0",
                     display: "flex",
                     alignItems: "center",
                     gap: "8px"
                  }}
               >
                  {part}
                  {index === 0 && module === "UD" && key_variable_tooltip && (
                     <TooltipInfo text={key_variable_tooltip} />
                  )}
               </div>
               );
            })}
         </div>
      );
   };

   const renderHeading = () => {
      if (!heading) return null;

      if (module === "UD" && typeof heading === "string") {
         return renderSplitHeading(heading);
      }

      const azzkuLike = module === "AZZKU" || module === "AZZKU_TOP";
      if (azzkuLike) {
         return (
            <>
               <div className="heading-screen-only">
                  <h3 style={{ marginBottom: "20px" }}>{heading}</h3>
               </div>
               {captureHeading && renderSplitHeading(captureHeading, "heading-capture-only")}
            </>
         );
      }

      return (
         <h3 style={{ marginBottom: "20px", display: 'flex', alignItems: 'center', gap: '8px' }}>
            {heading}
         </h3>
      );
   };

   return (
      <div className="bar-chart-box">

         {renderHeading()}

         {hasNoData ? (
            <div className="no-data-message">
               <div className="no-data-icon">📊</div>
               <h4>Brak danych</h4>
               <p>Dla wybranych kryteriów nie ma dostępnych danych do wyświetlenia.</p>
            </div>
         ) : (
            <>
         <div className="chart-wrap"> 
            <ResponsiveContainer width="100%" height="100%" minWidth={650}>
            <ComposedChart
               data={ data }
               margin={{
                  top: CHART_MARGIN_TOP,
                  right: 6,
                  left: 0,
                  bottom: vertical_labels ? labels_height : 0
               }}
            >

               { show_main_lines && 
                  <CartesianGrid vertical={ false } strokeWidth={1.3}/>
               }

               { show_auxiliary_lines && 
                  <CartesianGrid 
                     vertical={ false }
                     strokeWidth={0.3}
                     horizontalCoordinatesGenerator={ (props) => {
                        const coords = [];
                        const top = props.offset.top;
                        const plotHeight = props.offset.height;
                        for ( let i = 0; i < plotHeight; i += 10 ) {
                           coords.push( top + i );
                        }
                        return coords;
                  }}/>
               }

               <XAxis 
                  dataKey="label"
                  height={ vertical_labels ? labels_height : undefined }
                  angle={ vertical_labels ? -90 : 0 } 
                  tick={ vertical_labels ? ({ x, y, payload }) => {
                     
                     const words = payload.value.split(' ');
                     
                     const lines = [];
                     let l = 0; 
                     let i = 0;
                     words.forEach( w => {
                        if (( l + w.length ) > longest_label_l ) {
                           i++;
                           l = 0;
                           lines[i] = w;
                        } else {
                           l += w.length + 1;
                           lines[i] = lines[i] || ""; 
                           lines[i] += w + " "; 
                        }
                     });

                     return (
                        <g transform={`translate(${x},${y})`}>
                           { lines.map(( line, index) => (
                              <text
                                 key={index}
                                 x={0}
                                 y={index * 18}
                                 dy={0}
                                 textAnchor="end"
                                 transform="rotate(-90)"
                                 fontSize={14}
                              >
                                 { line }
                              </text>
                           ))}
                        </g>
                     );
                  } : { fontSize: 14 } }
               />

               <YAxis
                  width={90}
                  domain={yAxisDomain}
                  tick={(props) => <YAxisTickAligned {...props} module={module} />}
               />

               <Tooltip content={ props => (
                  <CustomTooltip 
                     show_average={ show_average }
                     bars_amount={ bars_amount }
                     legend={ legend }
                     {...props }
                  />
               )}/>
               
               {showZeroLine && (
                  <ReferenceLine 
                     y={0} 
                     stroke="#999" 
                     strokeWidth={1.5} 
                     label={{ value: "0", position: "right", fill: "#666", fontSize: 11 }}
                  />
               )}
               
               { Bars }

               { LineComponent }

            </ComposedChart>
            </ResponsiveContainer>
         </div>

         { legend && !!legend.length && 
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 30px", marginTop: "16px", justifyContent: "center" }}>
               { legend.map(( l, i ) => {
                  const color = getColorForCategory(l, legend, colorPalette, i);
                  return (
                     <div key={ l } style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        { onColorChange ? (
                           <ColorPickerAction
                              currentColor={color}
                              onChange={(newColor) => onColorChange(i, newColor)}
                              title={`Zmień kolor dla: ${l}`}
                              allowedColors={pickerPalette}
                           />
                        ) : (
                           <div style={{ width: "26px", height: "26px", background: color, borderRadius: "4px" }}/>
                        )}
                        <span> { l } </span>
                     </div>
                  );
               })}
            </div>
         }

         { (!legend || !legend.length) && onColorChange &&
            <div className="color-picker-single" style={{ display: "flex", gap: "12px", marginTop: "16px", justifyContent: "center", alignItems: "center" }}>
               <span style={{ fontSize: "0.85em", color: "#666" }}>Kolor:</span>
               <ColorPickerAction
                  currentColor={colorPalette[0]}
                  onChange={(newColor) => onColorChange(0, newColor)}
                  title="Zmień kolor serii"
                  allowedColors={pickerPalette}
               />
            </div>
         }
            </>
         )}
      </div>
   );
}